import { ActionIcon, Avatar, Badge, Button, Flex, Text, Tooltip } from "@mantine/core";

import {
  INCOMING_INSPECTION_KEY,
  mutateIncomingInspection,
} from "@/api/incomingInspection/useIncomingInspection";
import { itemTypeColor } from "@/constants/items";
import { AbstractItemDetailForm } from "@/features/abstractItem/form/DetailForm";
import { GradeDetailForm } from "@/features/grade/components/form/GradeDetailForm";
import { IncomingInspectionDetailForm } from "@/features/incomingInspection/form/DetailForm";
import { IncomingInspectionUpdateForm } from "@/features/incomingInspection/form/UpdateForm";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { ItemCategoryDetailForm } from "@/features/itemCategory/form/DetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { DateHeader } from "@/features/workLog/DateHeader";
import { useLoader } from "@/hooks/useLoader";
import { SpcInstance } from "@/instance/axios";
import { handleErrorResponse } from "@/utils/errorMessage";
import { dataStyle, excelDownLoad, headerStyle, numberStyle } from "@/utils/excelDownLoad";
import timeUtil from "@/utils/timeUtil";
import {
  IncomingInspectionsGet200Response,
  IncomingInspectionsGet200ResponseRowsInner,
  InventoriesBetweenGet200Response,
  ItemGradeViewGet200ResponseRowsInnerItemTypeEnum,
  SpcApiIncomingInspectionsIncomingInspectionIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconEdit,
  IconEye,
  IconFileSpreadsheet,
  IconFilterPlus,
  IconRecycle,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const IncomingInspectionPage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { setLoading, LoadingOverlay } = useLoader();
  const { openModal, closeModal } = useModal();
  const { setQuery, startDate, endDate, resetQueryStrings } =
    useStandardLayout<AxiosResponse<IncomingInspectionsGet200Response>>();

  const { mutate: deleteMutate } = useMutation(
    (params: SpcApiIncomingInspectionsIncomingInspectionIdDeleteRequest) =>
      mutateIncomingInspection.delete(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([INCOMING_INSPECTION_KEY]);
        customAlert("입고/수입검사 삭제에 성공하였습니다.", "삭제 성공", "green");
        closeModal({});
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "입고/수입검사" }),
          "입고/수입검사 삭제 실패",
          "red",
        );
      },
    },
  );

  //품목군(원자재, 부자재, 반제품, 제품) → 품목명 → Grade → Color → 형태
  const columns: readonly Column<IncomingInspectionsGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "itemType",
      name: "품목군",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Badge
            size="lg"
            color={
              itemTypeColor[
                row.itemWithGrade?.itemType as ItemGradeViewGet200ResponseRowsInnerItemTypeEnum
              ]
            }
          >
            {t(row.itemWithGrade?.itemType as ItemGradeViewGet200ResponseRowsInnerItemTypeEnum)}
          </Badge>
        );
      },
    },
    {
      key: "itemCode",
      name: "품목명",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(<ItemsDetailForm itemCode={row.itemWithGrade?.code} />, null, "")
            }
          >
            {row.itemWithGrade?.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "gradeName",
      name: "Grade",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <GradeDetailForm gradeCode={row.itemWithGrade?.gradeCode} />,
                null,
                "Grade 상세",
              )
            }
          >
            {row.itemWithGrade?.gradeName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemCategoryName",
      name: "Color",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemCategoryDetailForm ItemCategoryName={row.itemWithGrade?.itemCategoryName} />,
                null,
                "Color 상세",
              )
            }
          >
            {row.itemWithGrade?.itemCategoryName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "abstractItemCode",
      name: "형태",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <AbstractItemDetailForm AbstractItemCode={row.itemWithGrade?.abstractItemCode} />,
                null,
                "형태 상세",
              )
            }
          >
            {row.itemWithGrade?.abstractItemCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "lot.name",
      name: "로트명",
      sortable: true,
      resizable: true,
      formatter: ({ row }: { row: IncomingInspectionsGet200ResponseRowsInner }) => (
        <Flex align="center">{row.lotData?.name}</Flex>
      ),
    },
    {
      key: "createdAt",
      name: "입고일",
      sortable: true,
      resizable: true,
      formatter: ({ row }: { row: IncomingInspectionsGet200ResponseRowsInner }) => {
        return <Flex align="center">{timeUtil(row?.createdAt as string)}</Flex>;
      },
    },
    {
      key: "partnerName",
      name: "거래처",
      sortable: true,
      resizable: true,
    },
    {
      key: "goodQuantity",
      name: "양품 수량",
      sortable: true,
      resizable: true,
    },
    {
      key: "goodBagCount",
      name: "양품 bag 수",
      sortable: true,
      resizable: true,
    },
    {
      key: "defectQuantity",
      name: "불량 수량",
      sortable: true,
      resizable: true,
    },
    {
      key: "defectBagCount",
      name: "불량 bag 수",
      sortable: true,
      resizable: true,
    },
    {
      key: "defectReason",
      name: "반품사유",
      sortable: true,
      resizable: true,
    },
    {
      key: "inspectionAt",
      name: "검사일자",
      sortable: true,
      resizable: true,
      formatter: ({ row }: { row: IncomingInspectionsGet200ResponseRowsInner }) => {
        return <Flex align="center">{timeUtil(row?.inspectionAt as string)}</Flex>;
      },
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const detailIncomingInspectionActionView = () => {
          openModal(
            <IncomingInspectionDetailForm formatterProps={formatterProps} />,
            "",
            "입고/수입검사",
            true,
          );
        };
        const updateIncomingInspectionActionView = () => {
          openModal(
            <IncomingInspectionUpdateForm formatterProps={formatterProps} />,
            "",
            " 입고/수입검사 수정",
            true,
          );
        };

        const deleteIncomingInspectionAction = () => {
          openModal(
            <ConfirmForm
              message="선택한 행을 삭제하시겠습니까?"
              yesCallback={() =>
                deleteMutate({ incomingInspectionId: formatterProps?.row.id as number })
              }
              noCallback={() => {}}
            />,
            null,
            "입고/수입검사 삭제",
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon variant="subtle" onClick={detailIncomingInspectionActionView} color="black">
              <IconEye />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={updateIncomingInspectionActionView}
              color="yellow"
            >
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deleteIncomingInspectionAction()}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const excelDownLoadAction = useCallback(async () => {
    try {
      setLoading(true);

      const data = await SpcInstance.incomingInspectionsFindPost({
        query: JSON.stringify({
          createdAt: { $between: [startDate, endDate] },
        }),
        populate: ["itemWithGrade", "lotData"],
      });

      const headerRow = [
        { v: "품목군", t: "s", s: headerStyle },
        { v: "품목명", t: "s", s: headerStyle },
        { v: "Grade", t: "s", s: headerStyle },
        { v: "Color", t: "s", s: headerStyle },
        { v: "형태", t: "s", s: headerStyle },
        { v: "로트명", t: "s", s: headerStyle },
        { v: "입고일", t: "s", s: headerStyle },
        { v: "거래처", t: "s", s: headerStyle },
        { v: "양품 수량", t: "s", s: headerStyle },
        { v: "양품 봉투 수", t: "s", s: headerStyle },
        { v: "불량 수량", t: "s", s: headerStyle },
        { v: "불량 봉투 수", t: "s", s: headerStyle },
        { v: "반품사유", t: "s", s: headerStyle },
        { v: "검사일자", t: "s", s: headerStyle },
      ];

      const dataRows = (data?.data ?? []).map((inspection) => [
        {
          v: inspection.itemWithGrade?.itemType ? t(inspection.itemWithGrade.itemType) : "",
          t: "s",
          s: dataStyle,
        },
        { v: inspection.itemWithGrade?.name ?? "", t: "s", s: dataStyle },
        {
          v: inspection.itemWithGrade?.gradeName ?? "",
          t: "s",
          s: dataStyle,
        },
        {
          v: inspection.itemWithGrade?.itemCategoryName ?? "",
          t: "s",
          s: dataStyle,
        },
        {
          v: inspection.itemWithGrade?.abstractItemCode ?? "",
          t: "s",
          s: dataStyle,
        },
        { v: inspection.lotData?.name ?? "", t: "s", s: dataStyle },
        {
          v: inspection.createdAt
            ? dayjs(inspection.createdAt).format("YYYY-MM-DD").toString()
            : "",
          t: "s",
          s: dataStyle,
        },
        { v: inspection.partnerName ?? "", t: "s", s: dataStyle },
        { v: inspection.goodQuantity ?? "", t: "s", s: numberStyle },
        { v: inspection.goodBagCount ?? "", t: "s", s: numberStyle },
        { v: inspection.defectQuantity ?? "", t: "s", s: numberStyle },
        { v: inspection.defectBagCount ?? "", t: "s", s: numberStyle },
        { v: inspection.defectReason ?? "", t: "s", s: dataStyle },
        {
          v: inspection.inspectionAt
            ? dayjs(inspection.inspectionAt).format("YYYY-MM-DD").toString()
            : "",
          t: "s",
          s: dataStyle,
        },
      ]);

      await excelDownLoad({
        headerRow,
        dataRows: dataRows,
        colWidths: Array(headerRow.length).fill(240),
        fileName: "입고/수입검사 내역",
      });
    } catch (error) {
      throw error;
    } finally {
      // 로딩 종료
      setLoading(false);
    }
  }, [setLoading, startDate, endDate]);

  useEffect(() => {
    setQuery((prev) => {
      // 기존 query에서 createdAt 조건이 이미 존재하는지 확인
      const hasCreatedAt = prev.some(
        (item) =>
          item.createdAt?.$between?.[0] === startDate && item.createdAt?.$between?.[1] === endDate,
      );

      if (hasCreatedAt) {
        // 조건이 동일하면 상태를 업데이트하지 않음
        return prev;
      }

      // 기존 query에서 createdAt 조건 제거
      const filteredQuery = prev.filter((item) => !item.createdAt);

      // 새로운 createdAt 조건 추가
      return [
        ...filteredQuery,
        {
          createdAt: {
            $between: [startDate, endDate],
          },
        },
      ];
    });
  }, [endDate, startDate, setQuery]);

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <LoadingOverlay />
      <Flex p="sm" justify="flex-end" columnGap="md">
        <Button color="teal" rightIcon={<IconFileSpreadsheet />} onClick={excelDownLoadAction}>
          엑셀 다운로드
        </Button>
        <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
          검색값 초기화
        </Button>
      </Flex>
      <DateHeader />
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>
          입고/수입검사 내역 리스트
        </Text>
        <Text fz="xs">우리 회사에서 다루는 입고/수입검사를 관리합니다.</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip
              label={
                '사용자 정의 필터입니다. "필터 추가" 또는 "정렬 추가" 버튼을 클릭하여 조건을 입력합니다.'
              }
            >
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<IncomingInspectionsGet200Response, IncomingInspectionsGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: IncomingInspectionsGet200ResponseRowsInner) => JSON.stringify(row)}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<InventoriesBetweenGet200Response> />
      </Flex>
    </Flex>
  );
};
